import { forwardRef, type SelectHTMLAttributes } from 'react'
import { cn } from '../lib/cn'

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        'h-10 w-full rounded-lg border border-border-default bg-surface-card px-3 text-sm text-text-primary shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  ),
)
Select.displayName = 'Select'

export type MultiSelectProps = {
  options: ReadonlyArray<{ value: string; label: string }>
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  className?: string
  id?: string
}

/** Checkbox list multi-select for role pickers and filters. */
export function MultiSelect({
  options,
  value,
  onChange,
  disabled,
  className,
  id,
}: MultiSelectProps) {
  const selected = new Set(value)

  function toggle(optionValue: string, checked: boolean) {
    const next = new Set(selected)
    if (checked) next.add(optionValue)
    else next.delete(optionValue)
    onChange([...next].sort())
  }

  return (
    <div id={id} className={cn('space-y-2', className)}>
      {options.map((opt) => (
        <label
          key={opt.value}
          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-surface-workspace"
        >
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-border-default text-brand-primary"
            checked={selected.has(opt.value)}
            disabled={disabled}
            onChange={(e) => toggle(opt.value, e.target.checked)}
          />
          <span className="text-text-primary">{opt.label}</span>
        </label>
      ))}
    </div>
  )
}
